import React from 'react';

export interface CreateContextFactoryArgs<TInitialContextValue> {
  /**
   * What the hook returns OUTSIDE the provider. Leave it out and the hook
   * throws there instead — the fault then names the missing provider, rather
   * than surfacing one screen later as `undefined` off a field the type says
   * is always present.
   */
  initialValue?: TInitialContextValue;
  devToolsName: string;
}

/**
 * Make utils package and export this.
 * Creates React context
 */
export default function createContextFactory<TInitialContextValue>({
  devToolsName,
  initialValue,
}: CreateContextFactoryArgs<TInitialContextValue>) {
  const Context = React.createContext<TInitialContextValue | undefined>(
    initialValue,
  );

  Context.displayName = devToolsName;

  const useContext = () => {
    const value = React.useContext(Context);
    if (value === undefined) {
      throw new Error(`useContext must be used inside ${devToolsName}`);
    }
    return value;
  };

  /**
   * For the consumer that is LEGITIMATELY rendered on both sides of the
   * provider — a modal reused by a page that has the context and by pages that
   * do not — and can answer for itself when it is absent. Distinct from
   * `initialValue`, which is a decision for the whole context: this one leaves
   * `useContext` throwing for every consumer that has no such answer, and
   * hands `undefined` only where the call site asked for it. Do not reach for
   * it to silence a provider that ought to be there.
   */
  const useOptionalContext = () => React.useContext(Context);

  return {
    ...Context,
    // the provider's `value` is the context's own type: `undefined` is the
    // "no provider" sentinel above, never something a provider hands down
    Provider: Context.Provider as React.Provider<TInitialContextValue>,
    useContext,
    useOptionalContext,
  };
}
